import React, { useState, useCallback, useRef, useEffect } from 'react';
import { IdealCustomerProfile, SignalSuggestion } from '../../types';
import {
  ConversationStep,
  WizardMessage,
  WizardFormData,
  SuggestionWizardProps,
} from '../../types/suggestionWizard';
import {
  WIZARD_TEXT,
  WIZARD_COMPANY_SIZES,
  WIZARD_INDUSTRIES,
  WIZARD_REGIONS,
  CUSTOMER_TYPE_OPTIONS,
  SELLING_MOTION_OPTIONS,
  getPriorityConfig,
} from '../../constants/suggestionWizard';
import { apiClient } from '../../lib/api';
import { isSignalSaved, toggleSaveSignal } from '../../utils/savedSignals';
import { genId } from '../../utils/idUtils';

interface UseWizardConversationProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSuggestion: SuggestionWizardProps['onSelectSuggestion'];
  onSelectMultiple?: SuggestionWizardProps['onSelectMultiple'];
  existingICPs: IdealCustomerProfile[];
  preSelectedIcp?: IdealCustomerProfile | null;
  onICPCreated?: (icp: IdealCustomerProfile) => void;
}

export const useWizardConversation = ({
  isOpen,
  onClose,
  onSelectSuggestion,
  existingICPs,
  preSelectedIcp,
  onICPCreated,
}: UseWizardConversationProps) => {
  // Visibility state for animations
  const [isVisible, setIsVisible] = useState(false);

  // Conversation state
  const [messages, setMessages] = useState<WizardMessage[]>([]);
  const [currentStep, setCurrentStep] = useState<ConversationStep>('ask_product');
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Form data collected through conversation
  const [formData, setFormData] = useState<WizardFormData>({
    productDescription: '',
    customerType: 'B2B',
    sellingMotion: 'sales-led',
    companySizes: [],
    industries: [],
    buyerTitles: '',
    targetRegions: [],
  });

  // Chip selection state
  const [selectedChips, setSelectedChips] = useState<Set<string>>(new Set());

  // Expanded description state for ICP cards
  const [expandedDescriptionId, setExpandedDescriptionId] = useState<string | null>(null);

  // Signal state
  const [signals, setSignals] = useState<SignalSuggestion[]>([]);
  const [selectedSignals, setSelectedSignals] = useState<Set<string>>(new Set());
  const [expandedSignalName, setExpandedSignalName] = useState<string | null>(null);

  // Current ICP context
  const [currentIcpId, setCurrentIcpId] = useState<string | null>(null);
  const [currentIcpName, setCurrentIcpName] = useState<string>('');

  // Loading state for run signal button (while fetching preview)
  const [isRunningSignal, setIsRunningSignal] = useState(false);

  // Saved signals tracking
  const [savedSignalNames, setSavedSignalNames] = useState<Set<string>>(new Set());

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const shouldScrollToUserMessage = useRef(false);
  const hasInitialized = useRef(false);

  // Cache generated signals per ICP to avoid re-fetching on re-open
  const signalCacheRef = useRef<Map<string, { signals: SignalSuggestion[]; savedNames: Set<string> }>>(new Map());

  // Scroll to last user message
  const scrollToLastUserMessage = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let element: HTMLElement | null = lastUserMessageRef.current;
    if (!element) {
      element = container.querySelector('[data-is-last="true"]');
    }

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Handle scroll after messages update
  useEffect(() => {
    if (shouldScrollToUserMessage.current) {
      shouldScrollToUserMessage.current = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToLastUserMessage();
        });
      });
    }
  }, [messages, scrollToLastUserMessage]);

  // Add AI message with typing effect
  const addAIMessage = useCallback((content: string, delay = 200): Promise<void> => {
    return new Promise((resolve) => {
      setIsTyping(true);
      setTimeout(() => {
        setMessages(prev => [...prev, { id: genId(), type: 'ai', content, animate: true }]);
        setIsTyping(false);
        const typingDuration = content.length * 12 + 150;
        setTimeout(resolve, typingDuration);
      }, delay);
    });
  }, []);

  // Add user message
  const addUserMessage = useCallback((content: string) => {
    shouldScrollToUserMessage.current = true;
    setMessages(prev => [...prev, { id: genId(), type: 'user', content }]);
  }, []);

  // Update chip selection
  const updateChipSelection = useCallback((chipValue: string) => {
    setSelectedChips(prev => {
      const next = new Set(prev);
      if (next.has(chipValue)) {
        next.delete(chipValue);
      } else {
        next.add(chipValue);
      }
      const selectedArray = Array.from(next);
      setInputValue(selectedArray.join(', '));
      return next;
    });
  }, []);

  // Clear all chip selections (without affecting input value)
  const clearChips = useCallback(() => {
    if (selectedChips.size > 0) {
      setSelectedChips(new Set());
    }
  }, [selectedChips.size]);

  // Generate signals from ICP
  const generateSignalsFromIcp = useCallback(async (icpId: string) => {
    try {
      const response = await apiClient.generateSignalInspirations(icpId);
      const sortedSignals = (response.signals || []).sort((a, b) => {
        return getPriorityConfig(b.priority).dots - getPriorityConfig(a.priority).dots;
      });
      setSignals(sortedSignals);
      setIsTyping(false);

      const savedNames = new Set(
        sortedSignals.filter(s => isSignalSaved(s.name, icpId)).map(s => s.name)
      );
      setSavedSignalNames(savedNames);

      // Cache signals for this ICP
      signalCacheRef.current.set(icpId, { signals: sortedSignals, savedNames });

      await addAIMessage(WIZARD_TEXT.SIGNALS_FOUND(sortedSignals.length), 200);
      setMessages(prev => [...prev, { id: genId(), type: 'signals', content: '', signals: sortedSignals }]);
      setCurrentStep('show_signals');
    } catch {
      setIsTyping(false);
      await addAIMessage(WIZARD_TEXT.ERROR_GENERIC, 300);
      setCurrentStep('ask_product');
    }
  }, [addAIMessage]);

  // Handle ICP selection
  const handleICPSelect = useCallback(async (icp: IdealCustomerProfile) => {
    setCurrentIcpId(icp.id);
    setCurrentIcpName(icp.name);

    addUserMessage(icp.name);
    await addAIMessage(WIZARD_TEXT.ICP_LOADING(icp.name), 300);
    setIsTyping(true);
    setCurrentStep('generating');

    await generateSignalsFromIcp(icp.id);
  }, [addAIMessage, addUserMessage, generateSignalsFromIcp]);

  // Generate signals from form data
  const generateSignals = useCallback(async () => {
    setIsTyping(true);
    setCurrentStep('generating');

    try {
      const newICP = await apiClient.createICPProfile({
        name: formData.productDescription.slice(0, 40),
        solution_products: formData.productDescription,
        customer_type: formData.customerType,
        selling_motion: formData.sellingMotion,
        company_sizes: formData.companySizes.length > 0 ? formData.companySizes : undefined,
        target_verticals: formData.industries.length > 0 ? formData.industries : undefined,
        titles: formData.buyerTitles || undefined,
        target_region: formData.targetRegions.length > 0 ? formData.targetRegions : undefined,
      });
      onICPCreated?.(newICP);

      setCurrentIcpId(newICP.id);
      setCurrentIcpName(newICP.name);

      const response = await apiClient.generateSignalInspirations(newICP.id);
      const sortedSignals = (response.signals || []).sort((a, b) => {
        return getPriorityConfig(b.priority).dots - getPriorityConfig(a.priority).dots;
      });
      setSignals(sortedSignals);
      setIsTyping(false);

      setSavedSignalNames(new Set());

      await addAIMessage(WIZARD_TEXT.SIGNALS_TAILORED(sortedSignals.length), 200);
      await addAIMessage(WIZARD_TEXT.SELECT_SIGNALS, 300);
      setMessages(prev => [...prev, { id: genId(), type: 'signals', content: '', signals: sortedSignals }]);
      setCurrentStep('show_signals');
    } catch {
      setIsTyping(false);
      await addAIMessage(WIZARD_TEXT.ERROR_GENERIC, 300);
      setCurrentStep('ask_product');
    }
  }, [formData, onICPCreated, addAIMessage]);

  // Ask about regions
  const askRegions = useCallback(async () => {
    await addAIMessage(WIZARD_TEXT.REGIONS_QUESTION, 300);
    setMessages(prev => [...prev, {
      id: genId(),
      type: 'chips',
      content: '',
      chips: WIZARD_REGIONS.map(r => ({ value: r, label: r })),
    }]);
    setCurrentStep('ask_regions');
  }, [addAIMessage]);

  // Handle option selection
  const handleOptionSelect = useCallback(async (value: string, label: string) => {
    addUserMessage(label);

    if (currentStep === 'ask_customer_type') {
      const newCustomerType = value as 'B2B' | 'B2C' | 'Both';
      setFormData(prev => ({ ...prev, customerType: newCustomerType }));

      const response = WIZARD_TEXT.CUSTOMER_TYPE_RESPONSES[newCustomerType] || "Great choice!";
      await addAIMessage(response, 400);
      await addAIMessage(WIZARD_TEXT.MOTION_QUESTION, 300);

      setMessages(prev => [...prev, {
        id: genId(),
        type: 'options',
        content: '',
        options: SELLING_MOTION_OPTIONS,
      }]);
      setCurrentStep('ask_motion');
    } else if (currentStep === 'ask_motion') {
      const newMotion = value as 'sales-led' | 'plg' | 'services';
      setFormData(prev => ({ ...prev, sellingMotion: newMotion }));

      const response = WIZARD_TEXT.MOTION_RESPONSES[value] || "Solid approach!";
      await addAIMessage(response, 400);

      if (formData.customerType === 'B2B' || formData.customerType === 'Both') {
        await addAIMessage(WIZARD_TEXT.COMPANY_SIZE_QUESTION, 300);
        setMessages(prev => [...prev, {
          id: genId(),
          type: 'chips',
          content: '',
          chips: WIZARD_COMPANY_SIZES.map(s => ({ value: s, label: s })),
          skipLabel: "Any size",
        }]);
        setCurrentStep('ask_company_sizes');
      } else {
        await askRegions();
      }
    }
  }, [currentStep, formData.customerType, addAIMessage, addUserMessage, askRegions]);

  // Handle "Generate now" - saves current selections and generates
  const handleGenerateNow = useCallback(async () => {
    const selected = Array.from(selectedChips);
    if (currentStep === 'ask_company_sizes' && selected.length > 0) {
      setFormData(prev => ({ ...prev, companySizes: selected }));
    } else if (currentStep === 'ask_industries' && selected.length > 0) {
      setFormData(prev => ({ ...prev, industries: selected }));
    } else if (currentStep === 'ask_regions' && selected.length > 0) {
      setFormData(prev => ({ ...prev, targetRegions: selected }));
    }
    setSelectedChips(new Set());

    addUserMessage("Generate now");
    await addAIMessage(WIZARD_TEXT.GENERATING, 300);
    await generateSignals();
  }, [currentStep, selectedChips, addUserMessage, addAIMessage, generateSignals]);

  // Handle text input submission
  const handleSubmit = useCallback(async () => {
    const value = inputValue.trim();
    const hasValue = value.length > 0;

    if (currentStep === 'ask_product' && !hasValue) return;

    setInputValue('');
    setSelectedChips(new Set());

    if (currentStep === 'ask_product') {
      addUserMessage(value);
      setFormData(prev => ({ ...prev, productDescription: value }));

      await addAIMessage(WIZARD_TEXT.PRODUCT_RESPONSE, 300);
      await addAIMessage(WIZARD_TEXT.CUSTOMER_TYPE_QUESTION, 300);

      setMessages(prev => [...prev, {
        id: genId(),
        type: 'options',
        content: '',
        options: CUSTOMER_TYPE_OPTIONS,
      }]);
      setCurrentStep('ask_customer_type');
    } else if (currentStep === 'ask_company_sizes') {
      addUserMessage(hasValue ? value : 'Any size');
      if (hasValue) {
        setFormData(prev => ({
          ...prev,
          companySizes: value.split(',').map(s => s.trim()).filter(Boolean),
        }));
      }

      await addAIMessage(WIZARD_TEXT.INDUSTRY_QUESTION, 300);
      setMessages(prev => [...prev, {
        id: genId(),
        type: 'chips',
        content: '',
        chips: WIZARD_INDUSTRIES.map(i => ({ value: i, label: i })),
      }]);
      setCurrentStep('ask_industries');
    } else if (currentStep === 'ask_industries') {
      addUserMessage(hasValue ? value : 'Any industry');
      if (hasValue) {
        setFormData(prev => ({
          ...prev,
          industries: value.split(',').map(s => s.trim()).filter(Boolean),
        }));
      }

      await addAIMessage(WIZARD_TEXT.TITLES_QUESTION, 300);
      setMessages(prev => [...prev, {
        id: genId(),
        type: 'text_input',
        content: '',
        inputPlaceholder: 'e.g., VP of Engineering, CTO, Head of Product...',
      }]);
      setCurrentStep('ask_titles');
    } else if (currentStep === 'ask_titles') {
      addUserMessage(hasValue ? value : 'Any title');
      if (hasValue) {
        setFormData(prev => ({ ...prev, buyerTitles: value }));
      }

      if (formData.customerType === 'B2B' || formData.customerType === 'Both') {
        await addAIMessage(WIZARD_TEXT.FINDING_SIGNALS, 300);
        await generateSignals();
      } else {
        await askRegions();
      }
    } else if (currentStep === 'ask_regions') {
      addUserMessage(hasValue ? value : 'Worldwide');
      if (hasValue) {
        setFormData(prev => ({
          ...prev,
          targetRegions: value.split(',').map(s => s.trim()).filter(Boolean),
        }));
      }

      await addAIMessage(WIZARD_TEXT.FINDING_SIGNALS, 300);
      await generateSignals();
    }
  }, [currentStep, inputValue, formData.customerType, addAIMessage, addUserMessage, generateSignals, askRegions]);

  // Handle key press in input
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // Handle signal toggle (single-select: only one signal at a time)
  const handleToggleSignal = useCallback((signalName: string) => {
    setSelectedSignals(prev => {
      if (prev.has(signalName)) {
        return new Set();
      }
      return new Set([signalName]);
    });
  }, []);

  // Handle save/unsave signal
  const handleSaveSignal = useCallback((e: React.MouseEvent, signal: SignalSuggestion) => {
    e.stopPropagation();
    if (!currentIcpId) return;

    const isSaved = toggleSaveSignal(signal, currentIcpId, currentIcpName);
    setSavedSignalNames(prev => {
      const next = new Set(prev);
      if (isSaved) {
        next.add(signal.name);
      } else {
        next.delete(signal.name);
      }

      // Keep the cache in sync so re-opening the wizard reflects the latest saved state
      const cached = signalCacheRef.current.get(currentIcpId);
      if (cached) {
        signalCacheRef.current.set(currentIcpId, { ...cached, savedNames: new Set(next) });
      }

      return next;
    });
  }, [currentIcpId, currentIcpName]);

  // Run selected signal — fetches preview before closing wizard
  const handleRunSignals = useCallback(async () => {
    if (selectedSignals.size === 0) return;

    const selectedSignalsList = signals.filter(s => selectedSignals.has(s.name));
    if (selectedSignalsList.length === 0) return;

    const signal = selectedSignalsList[0];
    const query = signal.search_criteria.join('. ');
    const type = signal.target_entity === 'people' ? 'people' : 'companies';

    // Show loading state on the Run button while fetching preview
    setIsRunningSignal(true);
    try {
      const preview = await apiClient.previewDataset(query, type);
      onSelectSuggestion(query, type, currentIcpId || undefined, preview);
    } catch {
      // Fall back: pass without preview data — user can manually Run from DefaultScreen
      onSelectSuggestion(query, type, currentIcpId || undefined);
    } finally {
      setIsRunningSignal(false);
    }
    handleClose();
  }, [selectedSignals, signals, currentIcpId, onSelectSuggestion]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  // Initialize conversation when opening
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true;
      requestAnimationFrame(() => setIsVisible(true));

      // Reset state
      setMessages([]);
      setInputValue('');
      setFormData({
        productDescription: '',
        customerType: 'B2B',
        sellingMotion: 'sales-led',
        companySizes: [],
        industries: [],
        buyerTitles: '',
        targetRegions: [],
      });
      setSelectedChips(new Set());
      setSignals([]);
      setSelectedSignals(new Set());
      setExpandedSignalName(null);
      setCurrentStep('ask_product');

      // Start conversation - always go to ICP creation flow
      setTimeout(async () => {
        if (preSelectedIcp) {
          // Check cache for previously generated signals
          const cached = signalCacheRef.current.get(preSelectedIcp.id);
          if (cached) {
            // Restore from cache — skip API call
            setCurrentIcpId(preSelectedIcp.id);
            setCurrentIcpName(preSelectedIcp.name);
            setSignals(cached.signals);
            setSavedSignalNames(cached.savedNames);
            addUserMessage(preSelectedIcp.name);
            await addAIMessage(WIZARD_TEXT.SIGNALS_FOUND(cached.signals.length), 200);
            setMessages(prev => [...prev, { id: genId(), type: 'signals', content: '', signals: cached.signals }]);
            setCurrentStep('show_signals');
          } else {
            // No cache — call API as usual
            handleICPSelect(preSelectedIcp);
          }
        } else {
          // Always start with ICP creation flow (asking about product)
          await addAIMessage(WIZARD_TEXT.WELCOME_NO_ICPS, 300);
          await addAIMessage(WIZARD_TEXT.WELCOME_PROMPT, 500);
          setCurrentStep('ask_product');
          setTimeout(() => inputRef.current?.focus(), 100);
        }
      }, 200);
    } else if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      hasInitialized.current = false;
    }
  }, [isOpen, addAIMessage, existingICPs, handleICPSelect, preSelectedIcp]);

  return {
    // State
    isVisible,
    messages,
    currentStep,
    inputValue,
    isTyping,
    isRunningSignal,
    signals,
    selectedSignals,
    selectedChips,
    expandedDescriptionId,
    expandedSignalName,
    currentIcpId,
    savedSignalNames,

    // Setters
    setInputValue,
    setExpandedDescriptionId,
    setExpandedSignalName,

    // Refs
    messagesEndRef,
    messagesContainerRef,
    inputRef,
    lastUserMessageRef,

    // Handlers
    handleClose,
    handleSubmit,
    handleKeyPress,
    handleOptionSelect,
    handleICPSelect,
    handleToggleSignal,
    handleSaveSignal,
    handleRunSignals,
    handleGenerateNow,
    updateChipSelection,
    clearChips,
  };
};

export default useWizardConversation;
